<template>
  <div class="my-4 mx-2 lg:mx-4">
    <pdl-callout v-if="bikeData.p1ConfigStatus === 'Sent'" kind="default">
      <template #content>
        <span v-if="b2b" v-html="bp('orderSubmittedB2b')" />
        <span v-else v-html="bp('orderSubmitted')" />
      </template>
    </pdl-callout>
    <pdl-callout v-else-if="bikeData.p1ConfigStatus === 'Ordered'" kind="success">
      <template #content>
        <span v-html="bp('orderCompleted')" />
      </template>
    </pdl-callout>
    <h2
      v-if="bikeData"
      qaid="details-header__bike-name"
      class="heading heading--md text-gray-100 mb-1"
      v-text="bikeData.modelName"
    />

    <div v-if="bikeEtaVisible" class="flex items-center text-gray-60 text-sm leading-none">
      <i qaid="rail-theme-info-eta-icon" class="material-icons md-18 mr-25">schedule</i>
      <span qaid="rail-theme-info-eta" v-html="`${bikeEta} ${bp('buildTime')}`" />
    </div>

    <div class="mt-2 flex justify-between">
      <span qaid="details-header__base-price-label" class="text-sm" v-html="bp('basePrice')" />
      <span v-if="isHidingPrices" qaid="details-header-base-price" class="text-sm" v-html="bp('priceVaries')" />
      <span
        v-else-if="bikeData && b2b && !consumerFriendlyMode"
        qaid="details-header-base-price"
        class="text-blue text-sm"
        v-html="bikeData.defaultDealerCost"
      />
      <span
        v-else-if="bikeData"
        qaid="details-header-base-price"
        class="text-blue text-sm"
        v-html="bikeData.defaultAdvertised"
      />
    </div>

    <div class="mt-2 flex justify-between">
      <span qaid="details-header__est-price-label" class="text-sm" v-html="bp('estPrice')" />
      <span v-if="isHidingPrices" qaid="details-header-base-price" class="text-sm" v-html="bp('priceVaries')" />
      <span
        v-else-if="bikeData && b2b && !consumerFriendlyMode"
        qaid="details-header-est-price"
        class="text-blue text-sm"
        v-html="bikeData.calculatedDealerCost"
      />
      <span
        v-else-if="bikeData"
        qaid="details-header-est-price"
        class="text-blue text-sm"
        v-html="bikeData.calculatedAdvertised"
      />
    </div>

    <p class="text-xs text-gray-60" v-html="bp('estPriceExcluding')" />

    <div class="buttons mt-3">
      <trek-button
        v-if="b2b && isAddToCartVisible"
        class="w-full md:w-auto"
        qaid="details-header__add-to-cart-btn"
        :disabled="!isEditable || isP1ConfigInCart"
        primary
        @click="addToCartValidation"
      >
        <span v-html="bp('addToCart')" />
      </trek-button>

      <trek-button
        v-else-if="!b2b"
        class="w-full md:w-auto"
        qaid="details-header__send-retailer-btn"
        primary
        :disabled="!isEditable"
        @click="handleSendToRetailerClick"
      >
        <span v-html="bp('sendRetailer')" />
      </trek-button>

      <trek-button
        ref="tippy_shareTooltipId"
        v-tippy="{
          html: '#share-popover',
          interactive: true,
          reactive: true,
          trigger: 'click',
          placement: 'bottom',
          flip: false,
          sticky: true,
          theme: 'flush',
        }"
        qaid="details-header__share-btn"
        icon-right
        icon="expand_more"
        :disabled="bikeData.p1ConfigStatus === 'Ordered'"
        secondary
      >
        <span v-html="bp('share')" />
      </trek-button>

      <trek-button qaid="details-header__save-btn" secondary :disabled="!isEditable || isP1ConfigInCart" @click="save">
        <span v-html="bp('save')" />
      </trek-button>
    </div>
    <pdl-callout v-if="isUnavailableWarningShown" kind="default">
      <template #content>
        <span qaid="project-one-unavailable-warning" v-html="bp('unavailableWarning')" />
      </template>
    </pdl-callout>

    <div class="mt-4">
      <project-one-summary-list
        v-for="module in summaryModules"
        :key="module.name"
        :base-properties="bpData"
        :name="module.name"
        :groups="module.groups"
        :has-images="module.name !== 'personalize'"
        :qaid="`details-content__${module.name}-summary-list`"
      />
    </div>

    <div v-cloak id="share-popover">
      <ul class="nav-list block-list text-left ">
        <li class="block-list__item py-0">
          <trek-button button text qaid="share-popover-email" icon="email" @click="initiateShareAction('EMAIL')">
            <span v-html="bp('popoverEmail')" />
          </trek-button>
        </li>

        <li class="block-list__item py-0">
          <trek-button
            button
            text
            qaid="share-popover-download-print"
            icon="file_download"
            @click="initiateShareAction('DOWNLOAD')"
          >
            <span v-if="b2b" v-html="bp('downloadSellSheetPDF')" />
            <span v-else v-html="`${bp('popoverDownload')} / ${bp('popoverPrint')}`" />
          </trek-button>
        </li>

        <li class="block-list__item py-0">
          <trek-button
            button
            text
            qaid="share-popover-copy-id"
            icon="content_copy"
            @click="initiateShareAction('COPY')"
          >
            <span v-html="bp('popoverCopyBuildId')" />
          </trek-button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import {mapState, mapMutations, mapActions} from 'vuex';
import TrekButton from '@/components/TrekButton.vue';
import ProjectOneSummaryList from '@/projectOne/components/summary/components/ProjectOneSummaryList';
import {PdlCallout} from '@pedal/pdl-callout';
import sellSheetDownload from '@/projectOne/mixins/downloadSellSheet';
import editableStatus from '@/projectOne/mixins/editableStatus';

const SHARE_ACTION = {
  EMAIL: 'EMAIL',
  DOWNLOAD: 'DOWNLOAD',
  COPY: 'COPY',
};

export default {
  name: 'ProjectOneSummaryDetails',

  components: {TrekButton, ProjectOneSummaryList, PdlCallout},

  mixins: [sellSheetDownload, editableStatus],

  computed: {
    ...mapState('projectOne', [
      'bikeData',
      'summaryModules',
      'bikeEta',
      'bikeEtaVisible',
      'selections',
      'queuedShareAction',
      'hasQueuedSaveAction',
      'isAddToCartVisible',
      'isSaveModalVisible',
      'hasSelectionsChanged',
      'bikePackage',
      'isP1ConfigInCart',
      'isHidingPrices',
    ]),

    ...mapState('backend', ['b2b', 'isUserLoggedIn', 'consumerFriendlyMode']),

    isSaved() {
      return this.bikeData?.code && this.bikeData?.description;
    },

    isUnavailableWarningShown() {
      return !this.bikeData.p1ConfigStatus || this.bikeData.p1ConfigStatus === 'Saved';
    },
  },

  created() {
    this.fetchModule({
      buildId: this.$route.params.buildId,
      module: 'summary',
    });

    if (this.hasQueuedSaveAction) {
      if (this.isUserLoggedIn) {
        this.save();
      } else {
        this.setHasQueuedSaveAction(false);
      }
    }

    // If we have any share action queued up, then initiate the share action and clear the vuex variable by passing undefined in it.
    if (this.queuedShareAction) {
      if (this.isUserLoggedIn) {
        this.initiateShareAction(this.queuedShareAction);
        // this will set the 'queuedShareAction' to undefined.
        this.setQueuedShareAction();
      } else {
        // this will set the 'queuedShareAction' to undefined, we don't want to loop through this function if the user clicks back or fails to log in
        this.setQueuedShareAction();
      }
    }

    if (!this.b2b && this.isHidingPrices) {
      this.formData.showPricing = false;
    }
  },

  methods: {
    // common entry point for all three type of share action.
    initiateShareAction(shareType) {
      this.closeTooltip();
      if (!this.isUserLoggedIn) {
        this.setCachedRedirectData({
          buildId: this.$route.params.buildId,
          redirectUrlB2b: window.location.href,
          redirectUrlB2c: window.location.href,
          redirectUrlAnonymous: window.location.href,
          selections: this.selections,
        });
        this.setQueuedShareAction(shareType);
        window.location.href = '/login/';
      } else if (!this.isSaved) {
        this.setIsSaveModalVisible(true);
        this.setOnSaveCallbackFunction(() => this.initiateShareAction(shareType));
      } else {
        switch (shareType) {
          case SHARE_ACTION.EMAIL:
            this.setIsSummaryEmailModalVisible(true);
            break;

          case SHARE_ACTION.DOWNLOAD:
            this.downloadPdf();
            break;

          case SHARE_ACTION.COPY:
            this.copyBuildId();
            break;
        }
      }
    },

    copyBuildId() {
      navigator.clipboard
        .writeText(location.href)
        .then(() => {
          this.$notify({
            type: 'success',
            message: this.bp('linkCopied'),
          });
        })
        .catch((err) => {
          console.error(err);
        });
    },

    async downloadPdf() {
      //   If we have unsaved selection in the vuex, first save them in backoffice and then download the sell sheet
      if (this.hasSelectionsChanged) {
        await this.saveConfig();
      }

      // These two property should be set false unlike b2b toolbar
      this.$set(this.formData, 'showHeader', false);
      this.$set(this.formData, 'showSizingSelections', false);

      //this is a mixin method
      this.downloadSellSheet();
    },

    closeTooltip() {
      let el = this.$refs['tippy_shareTooltipId'];

      if (el && el.$el._tippy) {
        el.$el._tippy.hide();
      }
    },

    handleSendToRetailerClick() {
      if (!this.isUserLoggedIn) {
        this.setCachedRedirectData({
          buildId: this.$route.params.buildId,
          redirectUrlB2b: window.location.href,
          redirectUrlB2c: window.location.href.replace('summary', 'send-to-retailer'),
          redirectUrlAnonymous: window.location.href,
          selections: this.selections,
        });
        window.location.href = '/login/';
      } else if (this.isUserLoggedIn && !this.b2b) {
        this.$router.push('send-to-retailer');
      }
    },

    save() {
      if (!this.isUserLoggedIn) {
        this.setCachedRedirectData({
          buildId: this.$route.params.buildId,
          redirectUrlB2b: window.location.href,
          redirectUrlB2c: window.location.href,
          redirectUrlAnonymous: window.location.href,
          selections: this.selections,
        });
        this.setHasQueuedSaveAction(true);
        window.location.href = '/login/';
      } else {
        if (this.isSaved) {
          this.saveConfig();
        } else {
          this.setIsSaveModalVisible(true);
        }
      }
    },

    ...mapActions('projectOne', ['saveConfig', 'fetchModule', 'addToCartValidation']),
    ...mapMutations('projectOne', [
      'setCachedRedirectData',
      'setIsSaveModalVisible',
      'setIsSummaryEmailModalVisible',
      'setHasQueuedSaveAction',
      'setQueuedShareAction',
      'setOnSaveCallbackFunction',
      'setPageLoading',
    ]),
  },
};
</script>
<style lang="scss">
#share-popover {
  .block-list {
    &__item {
      button {
        width: 100%;
        @apply justify-start;
        @apply p-2;

        &:hover span {
          text-decoration: underline;
        }
      }
    }
  }
}
</style>
